import type { GID, UnlikelyCollection } from "@unlikelystudio/commerce-connector"

import type { Nullish } from "~/@types/generics"
import type { BundleOptionEntries } from "~/components/ui/Product/ProductHeader/_data/serialize-bundle-selector"
import type { TProductColor } from "~/components/ui/Product/ProductHeader/_data/serialize-product-color"
import { PRODUCT_CUSTOM_ATTRIBUTES, type BundleProductItemCustomAttribute } from "~/lib/shopify/constants"
import type { TVariant } from "~/lib/shopify/serializers/serialize-variants"
import type { UnlikelyCartLineItemInput } from "~/lib/shopify/types"
import { serializeItemIdFromProductInfos, type CartLineItemPayload } from "~/providers/GTMTrackingProvider/constants"
import { objectEntries } from "~/utils/object-entries"

type BundleLineItemProduct = {
  id: Nullish<GID>
  collectionIds: GID[]
  color: TProductColor
  bundleOptions: Nullish<BundleOptionEntries>
  collection: Nullish<string | UnlikelyCollection>
  title: Nullish<string>
}

export function serializeBundleLineItem({
  product,
  productUrl,
  currentVariant,
  cartLength,
  additionnalAttributes,
}: {
  product: BundleLineItemProduct
  productUrl: Nullish<string>
  currentVariant: Nullish<TVariant>
  cartLength: number
  additionnalAttributes?: UnlikelyCartLineItemInput["attributes"]
}): Nullish<UnlikelyCartLineItemInput> {
  if (!currentVariant?.id) {
    return null
  }

  // If the bundle is a single option bundle, we can skip the customAttributesData processing
  const isSingleOptionBundle = currentVariant?.selectedOptions?.length === 1

  // Getting the Current bundle color
  const currentColor = product?.color

  // Storing this data for afterwards
  let customAttributesData = {}

  if (isSingleOptionBundle) {
    if (product?.bundleOptions) {
      customAttributesData = (
        objectEntries(product?.bundleOptions).map(([key, value]) => {
          return {
            size: { title: value?.displayName ?? key, value: value?.options?.[0]?.value },
            color: {
              image: value?.color?.image,
              value: value?.color?.value,
            },
          }
        }) ?? []
      )?.filter(Boolean) satisfies BundleProductItemCustomAttribute[]
    }
  } else {
    // This code defines a constant `customAttributesData` which processes the `selectedOptions` of the `currentVariant`.
    // It maps over each `option` in `selectedOptions` and performs the following steps:
    // 1. Checks if the `option`, `option.name`, or `option.value` are not defined. If any of these are not defined, it returns `null`.
    // 2. Retrieves the corresponding bundle option from `productHeader.bundleOptions` using the `option.name`.
    // 3. Constructs an object with `size` and `color` properties:
    //    - `size` contains a `title` (either the `displayName` of the corresponding bundle option or the `option.name`) and the `option.value`.
    //    - `color` contains an `image` and a `value` from the corresponding bundle option's color properties.
    // 4. Filters out any `null` values from the resulting array.
    // 5. Ensures that the resulting array satisfies the `BundleProductItemCustomAttribute[]` type.
    if (product?.bundleOptions) {
      customAttributesData = (
        objectEntries(product?.bundleOptions)?.map(([key, value], index) => {
          const splittedVariantTitle = currentVariant?.title?.split(" / ")?.[index] ?? currentVariant?.title

          const option = value?.options.find(({ value }) => {
            return splittedVariantTitle && splittedVariantTitle.indexOf(value) > -1
          })

          return {
            size: { title: value?.displayName ?? key, value: option?.value },
            color: {
              image: value?.color?.image,
              value: value?.color?.value,
            },
          }
        }) ?? []
      )?.filter(Boolean) satisfies BundleProductItemCustomAttribute[]
    }
  }

  const collection = typeof product.collection === "string" ? product.collection : product.collection?.title

  return {
    quantity: 1,
    merchandiseId: currentVariant?.id,
    attributes: [
      { key: PRODUCT_CUSTOM_ATTRIBUTES.PRODUCT_URL, value: productUrl ?? "" },
      { key: PRODUCT_CUSTOM_ATTRIBUTES.PRODUCT_COLOR, value: JSON.stringify(currentColor) },
      { key: PRODUCT_CUSTOM_ATTRIBUTES.BUNDLE_PRODUCT, value: JSON.stringify(customAttributesData) },
      {
        key: PRODUCT_CUSTOM_ATTRIBUTES.COLLECTION_IDS,
        value: JSON.stringify(product?.collectionIds),
      },
      // Used inside serializeCartPayload
      ...(collection ? [{ key: PRODUCT_CUSTOM_ATTRIBUTES.COLLECTION, value: collection }] : []),
      {
        key: PRODUCT_CUSTOM_ATTRIBUTES.TRACKING_DATA,
        value: JSON.stringify({
          item_variant: `${currentVariant?.title ?? ""}-${currentColor?.value}`,
          item_id:
            product?.id && currentVariant?.id ? serializeItemIdFromProductInfos(product?.id, currentVariant?.id) : "",
          item_name: product?.title ?? "",
          item_sku: currentVariant?.sku ?? "",
          item_url: productUrl,
          item_image_url: currentVariant?.image?.[0]?.src ?? "",
          price: currentVariant?.unserializedPrice?.amount ?? null,
          compare_at_price: currentVariant?.unserializedPrice?.amount ?? null,
          currency: currentVariant?.unserializedPrice?.currencyCode ?? "",
          quantity: 1,
          item_category: collection ?? "",
          index: cartLength,
        } satisfies CartLineItemPayload),
      },
      ...(additionnalAttributes ?? []),
    ],
  }
}
